import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  PlusIcon,
  TrashIcon,
  UserGroupIcon,
  ClockIcon,
  ChevronDownIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  SparklesIcon,
  UserCircleIcon,
  TrophyIcon,
  CheckIcon,
  PrinterIcon,
} from '@heroicons/react/24/outline';
import ScorerAssignmentManager from './ScorerAssignmentManager';
import { supabase, fetchHoleScoresPaginated } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import AutoPairModal from './AutoPairModal';
import { calcRoundScore, type ScoringMethod } from '../lib/scoring';

interface Round {
  id: string;
  tournament_id: string;
  round_number: number;
  name: string;
  date: string | null;
  sort_order: number;
}

interface RankedPlayer extends Player {
  rank: number;
  total_score: number;
  rounds_played: number;
}

interface Player {
  id: string;
  first_name: string;
  last_name: string;
  category: string;
  handicap: number;
  resident_club: string;
  country: string;
  registration_number?: string;
  status: string;
}

interface Pairing {
  id: string;
  round_id: string;
  group_number: number;
  player_id: string;
  tee_time: string | null;
  starting_hole: number | null;
  sort_order: number;
  player?: Player;
}

interface Group {
  group_number: number;
  tee_time: string | null;
  starting_hole: number | null;
  pairings: Pairing[];
}

interface Props {
  tournamentId: string;
}

function TeeTimeInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <ClockIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
      <input
        type="time"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="text-xs border border-gray-200 rounded px-1.5 py-0.5 focus:outline-none focus:border-golf-orange w-[90px]"
      />
    </div>
  );
}

export default function PlayerPairingsTab({ tournamentId }: Props) {
  const [rounds, setRounds] = useState<Round[]>([]);
  const [players, setPlayers] = useState<Player[]>([]);
  const [selectedRoundId, setSelectedRoundId] = useState<string>('');
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [pairingsLoading, setPairingsLoading] = useState(false);
  const [tablesExist, setTablesExist] = useState(true);

  const [addingToGroup, setAddingToGroup] = useState<number | null>(null);
  const [playerSearch, setPlayerSearch] = useState('');
  const [deletingPairing, setDeletingPairing] = useState<Pairing | null>(null);
  const [savingTeeTime, setSavingTeeTime] = useState<number | null>(null);
  const [localTeeTimes, setLocalTeeTimes] = useState<Record<number, string>>({});
  const [localStartingHoles, setLocalStartingHoles] = useState<Record<number, number>>({});
  const [showAutoPair, setShowAutoPair] = useState(false);
  const [autoGenerating, setAutoGenerating] = useState(false);
  const [rankedPlayers, setRankedPlayers] = useState<RankedPlayer[]>([]);
  const [qualifyingRoundNumber, setQualifyingRoundNumber] = useState<number | null>(null);
  const [clearingGroup, setClearingGroup] = useState<number | null>(null);
  const [showClearAll, setShowClearAll] = useState(false);
  const [clearingAll, setClearingAll] = useState(false);
  const [showScorerAssignment, setShowScorerAssignment] = useState(false);
  const [showQualifierSelection, setShowQualifierSelection] = useState(false);
  const [qualifierPlayers, setQualifierPlayers] = useState<(Player & { total_score: number; rounds_played: number; rank: number })[]>([]);
  const [selectedQualifierIds, setSelectedQualifierIds] = useState<Set<string>>(new Set());
  const [topNCutoff, setTopNCutoff] = useState(0);
  const [qualifierSourceRounds, setQualifierSourceRounds] = useState<string[]>([]);
  const [loadingQualifiers, setLoadingQualifiers] = useState(false);
  const [manualAddSearch, setManualAddSearch] = useState('');

  useEffect(() => {
    fetchRoundsAndPlayers();
  }, [tournamentId]);

  useEffect(() => {
    if (selectedRoundId) {
      fetchPairings(selectedRoundId);
    } else {
      setGroups([]);
    }
  }, [selectedRoundId]);

  const fetchRoundsAndPlayers = async () => {
    setLoading(true);
    try {
      const [roundsRes, playersRes] = await Promise.all([
        supabase
          .from('tournament_rounds')
          .select('*')
          .eq('tournament_id', tournamentId)
          .order('sort_order')
          .order('round_number'),
        supabase
          .from('players')
          .select('id, first_name, last_name, category, handicap, resident_club, country, registration_number, status')
          .eq('tournament_id', tournamentId)
          .eq('status', 'Active')
          .order('first_name'),
      ]);

      if (roundsRes.error) {
        if (roundsRes.error.code === '42P01' || roundsRes.error.code === 'PGRST200' || roundsRes.error.code === '42703') {
          setTablesExist(false);
        } else {
          toast.error('Failed to load rounds');
        }
      } else {
        setRounds(roundsRes.data ?? []);
        if (roundsRes.data && roundsRes.data.length > 0) {
          setSelectedRoundId(roundsRes.data[0].id);
        }
      }

      if (!playersRes.error) {
        setPlayers(playersRes.data ?? []);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchPairings = async (roundId: string) => {
    setPairingsLoading(true);
    try {
      const { data: pairingRows, error } = await supabase
        .from('player_pairings')
        .select('*')
        .eq('round_id', roundId)
        .order('group_number')
        .order('sort_order');

      if (error) {
        const tableNotFound =
          error.code === '42P01' ||
          error.code === 'PGRST200' ||
          error.code === 'PGRST106' ||
          error.code === '42703' ||
          (error.message ?? '').toLowerCase().includes('does not exist') ||
          (error.message ?? '').toLowerCase().includes('relation') ||
          (error.message ?? '').toLowerCase().includes('player_pairings');
        if (tableNotFound) {
          setTablesExist(false);
        } else {
          console.error('Pairings load error:', error);
          toast.error('Failed to load pairings');
        }
        setGroups([]);
        return;
      }

      const rows = pairingRows ?? [];

      const playerIds = [...new Set(rows.map((r) => r.player_id))];
      const playerMap = new Map<string, Player>();

      if (playerIds.length > 0) {
        const { data: playerRows } = await supabase
          .from('players')
          .select('id, first_name, last_name, category, handicap, resident_club, country, registration_number, status')
          .in('id', playerIds);

        (playerRows ?? []).forEach((p: Player) => playerMap.set(p.id, p));
      }

      const pairings: Pairing[] = rows.map((r) => ({
        ...r,
        player: playerMap.get(r.player_id),
      }));

      const groupMap = new Map<number, Group>();

      for (const pairing of pairings) {
        if (!groupMap.has(pairing.group_number)) {
          groupMap.set(pairing.group_number, {
            group_number: pairing.group_number,
            tee_time: pairing.tee_time,
            starting_hole: pairing.starting_hole ?? 1,
            pairings: [],
          });
        }
        groupMap.get(pairing.group_number)!.pairings.push(pairing);
        if (pairing.tee_time) {
          groupMap.get(pairing.group_number)!.tee_time = pairing.tee_time;
        }
        if (pairing.starting_hole) {
          groupMap.get(pairing.group_number)!.starting_hole = pairing.starting_hole;
        }
      }

      const sortedGroups = Array.from(groupMap.values()).sort(
        (a, b) => a.group_number - b.group_number
      );
      setGroups(sortedGroups);

      const teeTimes: Record<number, string> = {};
      const startingHoles: Record<number, number> = {};
      sortedGroups.forEach((g) => {
        teeTimes[g.group_number] = g.tee_time ? g.tee_time.substring(0, 5) : '';
        startingHoles[g.group_number] = g.starting_hole ?? 1;
      });
      setLocalTeeTimes(teeTimes);
      setLocalStartingHoles(startingHoles);
    } finally {
      setPairingsLoading(false);
    }
  };

  const addGroup = async () => {
    if (!selectedRoundId) return;
    const nextGroup =
      groups.length > 0
        ? Math.max(...groups.map((g) => g.group_number)) + 1
        : 1;
    setGroups((prev) => [
      ...prev,
      { group_number: nextGroup, tee_time: null, starting_hole: 1, pairings: [] },
    ]);
    setLocalTeeTimes((prev) => ({ ...prev, [nextGroup]: '' }));
    setLocalStartingHoles((prev) => ({ ...prev, [nextGroup]: 1 }));
    toast.success(`Group ${nextGroup} created`);
  };

  const removeEmptyGroup = (groupNumber: number) => {
    const group = groups.find((g) => g.group_number === groupNumber);
    if (!group) return;
    if (group.pairings.length > 0) {
      toast.error('Remove all players from the group first');
      return;
    }
    setGroups((prev) => prev.filter((g) => g.group_number !== groupNumber));
  };

  const addPlayerToGroup = async (groupNumber: number, player: Player) => {
    if (!selectedRoundId) return;

    const alreadyPaired = groups.some((g) =>
      g.pairings.some((p) => p.player_id === player.id)
    );
    if (alreadyPaired) {
      toast.error(`${player.first_name} ${player.last_name} is already in a group`);
      return;
    }

    const group = groups.find((g) => g.group_number === groupNumber);
    const sortOrder = group ? group.pairings.length : 0;
    const teeTime = localTeeTimes[groupNumber] || null;
    const startingHole = localStartingHoles[groupNumber] ?? 1;

    let { data, error } = await supabase
      .from('player_pairings')
      .insert({
        tournament_id: tournamentId,
        round_id: selectedRoundId,
        group_number: groupNumber,
        player_id: player.id,
        tee_time: teeTime || null,
        starting_hole: startingHole,
        sort_order: sortOrder,
      })
      .select('*')
      .single();

    if (error && (error.code === '42703' || error.code === 'PGRST204')) {
      const fallback = await supabase.from('player_pairings').insert({
        tournament_id: tournamentId,
        round_id: selectedRoundId,
        group_number: groupNumber,
        player_id: player.id,
        tee_time: teeTime || null,
        sort_order: sortOrder,
      }).select('*').single();
      data = fallback.data;
      error = fallback.error;
    }

    if (error) {
      toast.error('Failed to add player to group');
      return;
    }

    const newPairing: Pairing = { ...data, player };

    setGroups((prev) =>
      prev.map((g) =>
        g.group_number === groupNumber
          ? { ...g, pairings: [...g.pairings, newPairing] }
          : g
      )
    );
    setPlayerSearch('');
    setAddingToGroup(null);
    toast.success(`${player.first_name} ${player.last_name} added to Group ${groupNumber}`);
  };

  const removePairing = async () => {
    if (!deletingPairing) return;
    const { error } = await supabase
      .from('player_pairings')
      .delete()
      .eq('id', deletingPairing.id);

    if (error) {
      toast.error('Failed to remove player');
      return;
    }

    setGroups((prev) =>
      prev.map((g) => ({
        ...g,
        pairings: g.pairings.filter((p) => p.id !== deletingPairing.id),
      }))
    );
    setDeletingPairing(null);
    toast.success('Player removed from group');
  };

  const saveTeeTime = async (groupNumber: number) => {
    if (!selectedRoundId) return;
    setSavingTeeTime(groupNumber);

    const rawTime = localTeeTimes[groupNumber] ?? '';
    const teeTime = rawTime || null;
    const startingHole = localStartingHoles[groupNumber] ?? 1;

    const group = groups.find((g) => g.group_number === groupNumber);
    if (!group || group.pairings.length === 0) {
      setGroups((prev) =>
        prev.map((g) =>
          g.group_number === groupNumber ? { ...g, tee_time: teeTime, starting_hole: startingHole } : g
        )
      );
      setSavingTeeTime(null);
      return;
    }

    const ids = group.pairings.map((p) => p.id);
    let { error } = await supabase
      .from('player_pairings')
      .update({ tee_time: teeTime, starting_hole: startingHole })
      .in('id', ids);

    if (error && (error.code === '42703' || error.code === 'PGRST204')) {
      const fallback = await supabase.from('player_pairings').update({ tee_time: teeTime }).in('id', ids);
      error = fallback.error;
    }

    if (error) {
      toast.error('Failed to save tee time');
    } else {
      setGroups((prev) =>
        prev.map((g) =>
          g.group_number === groupNumber ? { ...g, tee_time: teeTime, starting_hole: startingHole } : g
        )
      );
      toast.success(`Tee time & starting hole saved for Group ${groupNumber}`);
    }
    setSavingTeeTime(null);
  };

  const fetchQualifyingScores = async () => {
    const selectedRound = rounds.find((r) => r.id === selectedRoundId);
    if (!selectedRound) return;

    const previousRounds = rounds.filter((r) => r.round_number < selectedRound.round_number);
    if (previousRounds.length === 0) {
      setRankedPlayers([]);
      setQualifyingRoundNumber(null);
      return;
    }

    const prevRoundNumbers = previousRounds.map((r) => r.round_number);
    const playerIds = players.map((p) => p.id);
    if (playerIds.length === 0) { setRankedPlayers([]); setQualifyingRoundNumber(null); return; }

    const holeRows = await fetchHoleScoresPaginated(tournamentId, playerIds);
    if (holeRows.length === 0) { setRankedPlayers([]); setQualifyingRoundNumber(null); return; }

    const { data: tournamentCourse } = await supabase
      .from('tournament_courses')
      .select('course_id')
      .eq('tournament_id', tournamentId)
      .maybeSingle();

    let holeParMap: Record<number, number> = {};
    let holeHcpMap: Record<number, number> = {};
    if (tournamentCourse?.course_id) {
      const { data: holesData } = await supabase
        .from('holes')
        .select('hole_number, par, handicap_index')
        .eq('course_id', tournamentCourse.course_id)
        .order('hole_number');
      if (holesData) {
        for (const h of holesData) {
          holeParMap[h.hole_number] = h.par;
          holeHcpMap[h.hole_number] = h.handicap_index ?? h.hole_number;
        }
      }
    }

    const { data: methodSetting } = await supabase
      .from('system_settings')
      .select('value')
      .eq('key', `scoring_method_${tournamentId}`)
      .maybeSingle();
    const method: ScoringMethod = (methodSetting?.value as ScoringMethod) || 'stroke_play_net';

    const pairedIds = new Set(groups.flatMap((g) => g.pairings.map((p) => p.player_id)));

    const requiredRoundCount = prevRoundNumbers.length;
    const scoreMap = new Map<string, { total: number; rounds: number }>();
    for (const player of players) {
      const playerHoles = holeRows.filter((h) => h.player_id === player.id && prevRoundNumbers.includes(h.round) && h.strokes > 0);
      if (playerHoles.length === 0) continue;

      const roundMap: Record<number, { hole: number; strokes: number }[]> = {};
      for (const h of playerHoles) {
        if (!roundMap[h.round]) roundMap[h.round] = [];
        roundMap[h.round].push({ hole: h.hole, strokes: h.strokes });
      }

      let total = 0;
      let roundsPlayed = 0;
      for (const [, holes] of Object.entries(roundMap)) {
        if (holes.length < 18) continue;
        total += calcRoundScore(holes, Math.floor(player.handicap ?? 0), holeParMap, holeHcpMap, method);
        roundsPlayed++;
      }

      if (roundsPlayed === requiredRoundCount) {
        scoreMap.set(player.id, { total, rounds: roundsPlayed });
      }
    }

    const scored = players
      .filter((p) => scoreMap.has(p.id) && !pairedIds.has(p.id))
      .map((p) => ({ ...p, total_score: scoreMap.get(p.id)!.total, rounds_played: scoreMap.get(p.id)!.rounds, rank: 0 }))
      .sort((a, b) => method === 'stableford_net' ? b.total_score - a.total_score : a.total_score - b.total_score);

    const ranked: RankedPlayer[] = scored.map((p, i) => ({ ...p, rank: i + 1 }));
    setRankedPlayers(ranked);
    setQualifyingRoundNumber(previousRounds[previousRounds.length - 1].round_number);
  };

  const handleAutoPair = async (config: {
    categories: string[];
    hcpMin: number;
    hcpMax: number;
    groupSize: number;
    startTeeTime: string;
    teeTimeInterval: number;
    mode: 'random' | 'qualifier';
    qualifierPairingStyle: 'sequential' | 'seeded';
    startingTeeMode: 'hole1' | 'hole10' | 'split';
  }) => {
    if (!selectedRoundId) return;
    setAutoGenerating(true);

    try {
      const pairedIds = new Set(groups.flatMap((g) => g.pairings.map((p) => p.player_id)));

      let orderedPlayers: Player[];

      if (config.mode === 'qualifier' && rankedPlayers.length > 0) {
        const eligibleRanked = rankedPlayers.filter(
          (p) =>
            !pairedIds.has(p.id) &&
            (config.categories.length === 0 || config.categories.includes(p.category)) &&
            p.handicap >= config.hcpMin &&
            p.handicap <= config.hcpMax
        );

        if (config.qualifierPairingStyle === 'seeded') {
          const n = eligibleRanked.length;
          const seeded: Player[] = [];
          for (let i = 0; i < Math.ceil(n / 2); i++) {
            seeded.push(eligibleRanked[i]);
            const mirror = n - 1 - i;
            if (mirror !== i && eligibleRanked[mirror]) seeded.push(eligibleRanked[mirror]);
          }
          orderedPlayers = seeded;
        } else {
          orderedPlayers = eligibleRanked;
        }
      } else {
        const eligible = players.filter(
          (p) =>
            !pairedIds.has(p.id) &&
            (config.categories.length === 0 || config.categories.includes(p.category)) &&
            p.handicap >= config.hcpMin &&
            p.handicap <= config.hcpMax
        );
        orderedPlayers = [...eligible].sort(() => Math.random() - 0.5);
      }

      const shuffled = orderedPlayers;

      const nextGroupNumber =
        groups.length > 0 ? Math.max(...groups.map((g) => g.group_number)) + 1 : 1;

      const [startHour, startMin] = config.startTeeTime.split(':').map(Number);
      const startMinutes = startHour * 60 + startMin;

      const totalChunks = Math.ceil(shuffled.length / config.groupSize);
      const halfPoint = Math.ceil(totalChunks / 2);

      const newGroups: Group[] = [];
      const inserts: {
        tournament_id: string;
        round_id: string;
        group_number: number;
        player_id: string;
        tee_time: string;
        starting_hole: number;
        sort_order: number;
      }[] = [];

      let chunk = 0;
      while (chunk * config.groupSize < shuffled.length) {
        const slice = shuffled.slice(chunk * config.groupSize, (chunk + 1) * config.groupSize);
        const groupNumber = nextGroupNumber + chunk;

        let teeTimeOffset = chunk;
        if (config.startingTeeMode === 'split' && chunk >= halfPoint) {
          teeTimeOffset = chunk - halfPoint;
        }
        const totalMinutes = startMinutes + teeTimeOffset * config.teeTimeInterval;
        const h = Math.floor(totalMinutes / 60) % 24;
        const m = totalMinutes % 60;
        const teeTime = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;

        let startingHole = 1;
        if (config.startingTeeMode === 'hole10') {
          startingHole = 10;
        } else if (config.startingTeeMode === 'split') {
          startingHole = chunk < halfPoint ? 1 : 10;
        }

        slice.forEach((player, idx) => {
          inserts.push({
            tournament_id: tournamentId,
            round_id: selectedRoundId,
            group_number: groupNumber,
            player_id: player.id,
            tee_time: teeTime,
            starting_hole: startingHole,
            sort_order: idx,
          });
        });

        chunk++;
      }

      if (inserts.length === 0) {
        toast.error('No eligible unpaired players found');
        return;
      }

      let { data: inserted, error } = await supabase
        .from('player_pairings')
        .insert(inserts)
        .select('*');

      if (error && (error.code === '42703' || error.code === 'PGRST204')) {
        const fallbackInserts = inserts.map(({ starting_hole, ...rest }) => rest);
        const fallback = await supabase.from('player_pairings').insert(fallbackInserts).select('*');
        inserted = fallback.data;
        error = fallback.error;
      }

      if (error) {
        toast.error('Failed to generate pairings');
        return;
      }

      const playerMap = new Map(players.map((p) => [p.id, p]));

      const groupMap = new Map<number, Group>();
      for (const row of inserted ?? []) {
        if (!groupMap.has(row.group_number)) {
          groupMap.set(row.group_number, {
            group_number: row.group_number,
            tee_time: row.tee_time,
            starting_hole: row.starting_hole ?? 1,
            pairings: [],
          });
        }
        groupMap.get(row.group_number)!.pairings.push({ ...row, player: playerMap.get(row.player_id) });
      }

      const sortedNew = Array.from(groupMap.values()).sort((a, b) => a.group_number - b.group_number);

      setGroups((prev) => [...prev, ...sortedNew]);
      setLocalTeeTimes((prev) => {
        const next = { ...prev };
        sortedNew.forEach((g) => {
          next[g.group_number] = g.tee_time ? g.tee_time.substring(0, 5) : '';
        });
        return next;
      });
      setLocalStartingHoles((prev) => {
        const next = { ...prev };
        sortedNew.forEach((g) => {
          next[g.group_number] = g.starting_hole ?? 1;
        });
        return next;
      });

      toast.success(`Generated ${sortedNew.length} group${sortedNew.length !== 1 ? 's' : ''} with ${inserts.length} players`);
      setShowAutoPair(false);
    } finally {
      setAutoGenerating(false);
    }
  };

  const clearGroup = async (groupNumber: number) => {
    if (!selectedRoundId) return;
    const group = groups.find((g) => g.group_number === groupNumber);
    if (!group || group.pairings.length === 0) return;

    const ids = group.pairings.map((p) => p.id);
    const { error } = await supabase
      .from('player_pairings')
      .delete()
      .in('id', ids);

    if (error) {
      toast.error('Failed to clear group');
      return;
    }

    setGroups((prev) => prev.filter((g) => g.group_number !== groupNumber));
    setClearingGroup(null);
    toast.success(`Group ${groupNumber} cleared`);
  };

  const clearAllPairings = async () => {
    if (!selectedRoundId) return;
    setClearingAll(true);
    try {
      const { error } = await supabase
        .from('player_pairings')
        .delete()
        .eq('round_id', selectedRoundId);

      if (error) {
        toast.error('Failed to clear pairings');
        return;
      }

      setGroups([]);
      setLocalTeeTimes({});
      setLocalStartingHoles({});
      setShowClearAll(false);
      toast.success('All pairings cleared');
    } finally {
      setClearingAll(false);
    }
  };

  const openQualifierSelection = async () => {
    const selectedRound = rounds.find((r) => r.id === selectedRoundId);
    if (!selectedRound) return;

    const previousRounds = rounds.filter((r) => r.round_number < selectedRound.round_number);
    if (previousRounds.length === 0) {
      toast.error('No qualifying rounds found before this round');
      return;
    }

    setLoadingQualifiers(true);
    setShowQualifierSelection(true);

    try {
      const prevRoundNumbers = previousRounds.map((r) => r.round_number);
      setQualifierSourceRounds(previousRounds.map((r) => r.name));

      const playerIds = players.map((p) => p.id);
      if (playerIds.length === 0) { setQualifierPlayers([]); setLoadingQualifiers(false); return; }

      const holeRows = await fetchHoleScoresPaginated(tournamentId, playerIds);
      if (holeRows.length === 0) { setQualifierPlayers([]); setLoadingQualifiers(false); return; }

      const { data: tournamentCourse } = await supabase
        .from('tournament_courses')
        .select('course_id')
        .eq('tournament_id', tournamentId)
        .maybeSingle();

      let holeParMap: Record<number, number> = {};
      let holeHcpMap: Record<number, number> = {};
      if (tournamentCourse?.course_id) {
        const { data: holesData } = await supabase
          .from('holes')
          .select('hole_number, par, handicap_index')
          .eq('course_id', tournamentCourse.course_id)
          .order('hole_number');
        if (holesData) {
          for (const h of holesData) {
            holeParMap[h.hole_number] = h.par;
            holeHcpMap[h.hole_number] = h.handicap_index ?? h.hole_number;
          }
        }
      }

      const { data: methodSetting } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', `scoring_method_${tournamentId}`)
        .maybeSingle();
      const method: ScoringMethod = (methodSetting?.value as ScoringMethod) || 'stroke_play_net';

      const pairedIds = new Set(groups.flatMap((g) => g.pairings.map((p) => p.player_id)));

      const requiredRounds = prevRoundNumbers.length;
      const scoreMap = new Map<string, { total: number; rounds: number }>();
      for (const player of players) {
        const playerHoles = holeRows.filter((h) => h.player_id === player.id && prevRoundNumbers.includes(h.round) && h.strokes > 0);
        if (playerHoles.length === 0) continue;

        const roundMap: Record<number, { hole: number; strokes: number }[]> = {};
        for (const h of playerHoles) {
          if (!roundMap[h.round]) roundMap[h.round] = [];
          roundMap[h.round].push({ hole: h.hole, strokes: h.strokes });
        }

        let total = 0;
        let roundsPlayed = 0;
        for (const [, holes] of Object.entries(roundMap)) {
          if (holes.length < 18) continue;
          total += calcRoundScore(holes, Math.floor(player.handicap ?? 0), holeParMap, holeHcpMap, method);
          roundsPlayed++;
        }

        if (roundsPlayed === requiredRounds) {
          scoreMap.set(player.id, { total, rounds: roundsPlayed });
        }
      }

      const ranked = players
        .filter((p) => scoreMap.has(p.id) && !pairedIds.has(p.id))
        .map((p) => {
          const info = scoreMap.get(p.id)!;
          return { ...p, total_score: info.total, rounds_played: info.rounds, rank: 0 };
        })
        .sort((a, b) => method === 'stableford_net' ? b.total_score - a.total_score : a.total_score - b.total_score)
        .map((p, i) => ({ ...p, rank: i + 1 }));

      setQualifierPlayers(ranked);

      // Apply qualifying cut rules based on category and handicap
      const getQualifyCut = (category: string, handicap: number): number => {
        const cat = category.toLowerCase();
        if (cat.includes('ladies') || cat.includes('women') || cat.includes('female')) {
          return handicap <= 18 ? 8 : 7;
        }
        if (cat.includes('veteran') || cat.includes('senior')) {
          return 15;
        }
        // Men (default)
        return handicap <= 18 ? 60 : 30;
      };

      // Select qualifiers per category/handicap group
      const selectedIds = new Set<string>();
      const categoryGroups = new Map<string, typeof ranked>();
      for (const p of ranked) {
        const key = `${p.category}_${p.handicap <= 18 ? 'low' : 'high'}`;
        if (!categoryGroups.has(key)) categoryGroups.set(key, []);
        categoryGroups.get(key)!.push(p);
      }

      for (const [, groupPlayers] of categoryGroups) {
        if (groupPlayers.length === 0) continue;
        const cut = getQualifyCut(groupPlayers[0].category, groupPlayers[0].handicap);
        const qualifiers = groupPlayers.slice(0, cut);
        for (const p of qualifiers) selectedIds.add(p.id);
      }

      const defaultCut = Math.min(ranked.length, selectedIds.size);
      setTopNCutoff(defaultCut);
      setSelectedQualifierIds(selectedIds);
    } catch {
      toast.error('Failed to load qualifying scores');
    } finally {
      setLoadingQualifiers(false);
    }
  };

  const confirmQualifierSelection = async () => {
    if (selectedQualifierIds.size === 0 || !selectedRoundId) return;

    setAutoGenerating(true);
    try {
      const selectedPlayers = qualifierPlayers.filter((p) => selectedQualifierIds.has(p.id));
      const nextGroupNumber = groups.length > 0 ? Math.max(...groups.map((g) => g.group_number)) + 1 : 1;
      const groupSize = 4;
      const newGroups: Group[] = [];
      const inserts: {
        tournament_id: string;
        round_id: string;
        group_number: number;
        player_id: string;
        tee_time: string | null;
        starting_hole: number;
        sort_order: number;
      }[] = [];

      let chunk = 0;
      while (chunk * groupSize < selectedPlayers.length) {
        const slice = selectedPlayers.slice(chunk * groupSize, (chunk + 1) * groupSize);
        const groupNum = nextGroupNumber + chunk;

        const pairings: Pairing[] = slice.map((p, idx) => ({
          id: `temp-${groupNum}-${idx}`,
          round_id: selectedRoundId,
          group_number: groupNum,
          player_id: p.id,
          tee_time: null,
          starting_hole: 1,
          sort_order: idx,
          player: p,
        }));

        newGroups.push({ group_number: groupNum, tee_time: null, starting_hole: 1, pairings });

        slice.forEach((p, idx) => {
          inserts.push({
            tournament_id: tournamentId,
            round_id: selectedRoundId,
            group_number: groupNum,
            player_id: p.id,
            tee_time: null,
            starting_hole: 1,
            sort_order: idx,
          });
        });

        chunk++;
      }

      const { error } = await supabase.from('player_pairings').insert(inserts);
      if (error) {
        if (error.message?.includes('starting_hole')) {
          const fallbackInserts = inserts.map(({ starting_hole: _, ...rest }) => rest);
          const { error: retryErr } = await supabase.from('player_pairings').insert(fallbackInserts);
          if (retryErr) throw retryErr;
        } else {
          throw error;
        }
      }

      setGroups((prev) => [...prev, ...newGroups]);
      setShowQualifierSelection(false);
      toast.success(`${selectedPlayers.length} players paired into ${newGroups.length} groups`);
    } catch (err: any) {
      toast.error(err.message || 'Failed to create pairings');
    } finally {
      setAutoGenerating(false);
    }
  };

  const printPairings = () => {
    const round = rounds.find((r) => r.id === selectedRoundId);
    if (!round || groups.length === 0) return;

    const dateStr = round.date
      ? new Date(round.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
      : '';

    const rows = groups
      .sort((a, b) => a.group_number - b.group_number)
      .map((g) => {
        const playerLines = g.pairings
          .sort((a, b) => a.sort_order - b.sort_order)
          .map((p) => {
            const pl = p.player;
            if (!pl) return '<tr><td colspan="4" style="padding:4px 8px;border-bottom:1px solid #eee;color:#999;">Unknown player</td></tr>';
            return `<tr>
              <td style="padding:4px 8px;border-bottom:1px solid #f0f0f0;font-size:13px;">${pl.first_name} ${pl.last_name}</td>
              <td style="padding:4px 8px;border-bottom:1px solid #f0f0f0;font-size:13px;color:#666;">${pl.category || ''}</td>
              <td style="padding:4px 8px;border-bottom:1px solid #f0f0f0;font-size:13px;text-align:center;">${pl.handicap ?? '-'}</td>
              <td style="padding:4px 8px;border-bottom:1px solid #f0f0f0;font-size:13px;color:#666;">${pl.country || ''}</td>
            </tr>`;
          })
          .join('');

        const teeTime = g.tee_time || '';
        const startingHole = g.starting_hole ? `Hole ${g.starting_hole}` : '';

        return `
          <div style="break-inside:avoid;margin-bottom:16px;border:1px solid #ddd;border-radius:8px;overflow:hidden;">
            <div style="background:#f7f7f7;padding:8px 12px;display:flex;justify-content:space-between;align-items:center;border-bottom:1px solid #ddd;">
              <strong style="font-size:14px;">Group ${g.group_number}</strong>
              <span style="font-size:12px;color:#555;">${[teeTime, startingHole].filter(Boolean).join(' | ')}</span>
            </div>
            <table style="width:100%;border-collapse:collapse;">
              <thead>
                <tr style="background:#fafafa;">
                  <th style="padding:4px 8px;text-align:left;font-size:11px;color:#888;font-weight:600;">Player</th>
                  <th style="padding:4px 8px;text-align:left;font-size:11px;color:#888;font-weight:600;">Category</th>
                  <th style="padding:4px 8px;text-align:center;font-size:11px;color:#888;font-weight:600;">HCP</th>
                  <th style="padding:4px 8px;text-align:left;font-size:11px;color:#888;font-weight:600;">Country</th>
                </tr>
              </thead>
              <tbody>${playerLines}</tbody>
            </table>
          </div>
        `;
      })
      .join('');

    const totalPlayers = groups.reduce((s, g) => s + g.pairings.length, 0);

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${round.name} - Pairings</title>
        <style>
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; padding: 24px; color: #222; }
          @media print { body { padding: 12px; } }
        </style>
      </head>
      <body>
        <div style="margin-bottom:20px;border-bottom:2px solid #222;padding-bottom:12px;">
          <h1 style="margin:0;font-size:22px;">${round.name}</h1>
          ${dateStr ? `<p style="margin:4px 0 0;font-size:13px;color:#555;">${dateStr}</p>` : ''}
          <p style="margin:4px 0 0;font-size:12px;color:#888;">${groups.length} groups &middot; ${totalPlayers} players</p>
        </div>
        <div style="columns:2;column-gap:20px;">
          ${rows}
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.onload = () => {
        printWindow.print();
      };
    }
  };

  const pairedPlayerIds = new Set(
    groups.flatMap((g) => g.pairings.map((p) => p.player_id))
  );

  const unpairedPlayers = players.filter((p) => !pairedPlayerIds.has(p.id));

  const filteredSearchPlayers = players.filter((p) => {
    if (pairedPlayerIds.has(p.id)) return false;
    if (!playerSearch.trim()) return true;
    const q = playerSearch.toLowerCase();
    return (
      p.first_name.toLowerCase().includes(q) ||
      p.last_name.toLowerCase().includes(q) ||
      (p.resident_club ?? '').toLowerCase().includes(q) ||
      (p.country ?? '').toLowerCase().includes(q)
    );
  });

  const selectedRound = rounds.find((r) => r.id === selectedRoundId);

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-golf-orange border-t-transparent" />
      </div>
    );
  }

  if (!tablesExist) {
    return (
      <div className="p-8 text-center">
        <UserGroupIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <h3 className="font-semibold text-golf-gray mb-2">Database setup required</h3>
        <p className="text-sm text-gray-500 max-w-lg mx-auto mb-4">
          The <code className="bg-gray-100 px-1 rounded">player_pairings</code> table does not
          exist yet. Please run the following SQL in your{' '}
          <strong>Supabase Dashboard &rarr; SQL Editor</strong>:
        </p>
        <pre className="text-left text-xs bg-gray-50 border border-gray-200 rounded-lg p-4 max-w-2xl mx-auto overflow-x-auto whitespace-pre-wrap">
{`CREATE TABLE IF NOT EXISTS player_pairings (
  id uuid PRIMARY KEY DEFAULT gen_random_uuid(),
  tournament_id uuid NOT NULL REFERENCES tournaments(id) ON DELETE CASCADE,
  round_id uuid NOT NULL REFERENCES tournament_rounds(id) ON DELETE CASCADE,
  group_number integer NOT NULL DEFAULT 1,
  player_id uuid NOT NULL REFERENCES players(id) ON DELETE CASCADE,
  tee_time time,
  sort_order integer NOT NULL DEFAULT 0,
  created_at timestamptz NOT NULL DEFAULT now(),
  UNIQUE(round_id, player_id)
);
CREATE INDEX IF NOT EXISTS player_pairings_tournament_id_idx ON player_pairings(tournament_id);
CREATE INDEX IF NOT EXISTS player_pairings_round_id_idx ON player_pairings(round_id);
CREATE INDEX IF NOT EXISTS player_pairings_player_id_idx ON player_pairings(player_id);
ALTER TABLE player_pairings ENABLE ROW LEVEL SECURITY;
CREATE POLICY "Auth read pairings" ON player_pairings FOR SELECT TO authenticated USING (true);
CREATE POLICY "Auth insert pairings" ON player_pairings FOR INSERT TO authenticated WITH CHECK (true);
CREATE POLICY "Auth update pairings" ON player_pairings FOR UPDATE TO authenticated USING (true) WITH CHECK (true);
CREATE POLICY "Auth delete pairings" ON player_pairings FOR DELETE TO authenticated USING (true);`}
        </pre>
        <p className="text-xs text-gray-400 mt-3">After running the SQL, refresh this page.</p>
      </div>
    );
  }

  if (rounds.length === 0) {
    return (
      <div className="p-8 text-center">
        <UserGroupIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <h3 className="font-semibold text-golf-gray mb-2">No rounds defined</h3>
        <p className="text-sm text-gray-500">
          Add Golf Rounds under the Tournament Details &gt; Categories &amp; Rounds tab first.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h3 className="text-lg font-semibold text-golf-gray">Player Pairings</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            {unpairedPlayers.length} of {players.length} players unpaired
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <select
              value={selectedRoundId}
              onChange={(e) => setSelectedRoundId(e.target.value)}
              className="appearance-none pl-3 pr-8 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-golf-orange bg-white"
            >
              {rounds.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                  {r.date
                    ? ` — ${new Date(r.date + 'T00:00:00').toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                      })}`
                    : ''}
                </option>
              ))}
            </select>
            <ChevronDownIcon className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>
          {groups.length > 0 && (
            <button
              onClick={() => setShowClearAll(true)}
              className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 text-red-500 text-sm rounded-lg hover:border-red-300 hover:bg-red-50 transition-colors"
            >
              <TrashIcon className="w-4 h-4" />
              Clear All
            </button>
          )}
          {groups.length > 0 && (
            <button
              onClick={printPairings}
              className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 text-golf-gray text-sm rounded-lg hover:border-golf-orange hover:text-golf-orange transition-colors"
            >
              <PrinterIcon className="w-4 h-4" />
              Print
            </button>
          )}
          <button
            onClick={() => setShowScorerAssignment(true)}
            disabled={!selectedRoundId}
            className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 text-golf-gray text-sm rounded-lg hover:border-golf-orange hover:text-golf-orange transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <UserCircleIcon className="w-4 h-4" />
            Scorers
          </button>
          {selectedRound && rounds.some((r) => r.round_number < selectedRound.round_number) && (
            <button
              onClick={openQualifierSelection}
              disabled={unpairedPlayers.length === 0}
              className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 text-golf-gray text-sm rounded-lg hover:border-emerald-500 hover:text-emerald-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <TrophyIcon className="w-4 h-4" />
              Select Qualifiers
            </button>
          )}
          <button
            onClick={async () => { await fetchQualifyingScores(); setShowAutoPair(true); }}
            disabled={unpairedPlayers.length === 0}
            className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 text-golf-gray text-sm rounded-lg hover:border-golf-orange hover:text-golf-orange transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <SparklesIcon className="w-4 h-4" />
            Auto-Pair
          </button>
          <button
            onClick={addGroup}
            className="flex items-center gap-1.5 px-3 py-2 bg-golf-orange text-white text-sm rounded-lg hover:bg-golf-orange/90 transition-colors"
          >
            <PlusIcon className="w-4 h-4" />
            Add Group
          </button>
        </div>
      </div>

      {pairingsLoading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-golf-orange border-t-transparent" />
        </div>
      ) : groups.length === 0 ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-16 bg-gray-50 rounded-xl border border-dashed border-gray-200"
        >
          <UserGroupIcon className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No groups yet for {selectedRound?.name}</p>
          <p className="text-sm text-gray-400 mt-1 mb-4">Create groups and assign players to tee times</p>
          <button
            onClick={addGroup}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-golf-orange text-white text-sm rounded-lg hover:bg-golf-orange/90 transition-colors"
          >
            <PlusIcon className="w-4 h-4" />
            Create First Group
          </button>
        </motion.div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <AnimatePresence>
            {groups.map((group) => (
              <motion.div
                key={group.group_number}
                layout
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.97 }}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden"
              >
                {/* Group header */}
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-7 h-7 rounded-full bg-golf-orange/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-bold text-golf-orange">{group.group_number}</span>
                    </div>
                    <span className="font-semibold text-golf-gray text-sm truncate">
                      Group {group.group_number}
                    </span>
                    <span className="text-xs text-gray-400">
                      {group.pairings.length} player{group.pairings.length !== 1 ? 's' : ''}
                    </span>
                    <span
                      className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                        (localStartingHoles[group.group_number] ?? 1) === 10
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-emerald-100 text-emerald-700'
                      }`}
                    >
                      H{localStartingHoles[group.group_number] ?? 1}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <select
                      value={localStartingHoles[group.group_number] ?? 1}
                      onChange={(e) => {
                        const hole = Number(e.target.value);
                        setLocalStartingHoles((prev) => ({ ...prev, [group.group_number]: hole }));
                      }}
                      className="text-xs border border-gray-200 rounded px-1 py-0.5 focus:outline-none focus:border-golf-orange w-[60px]"
                      title="Starting hole"
                    >
                      <option value={1}>H1</option>
                      <option value={10}>H10</option>
                    </select>
                    <TeeTimeInput
                      value={localTeeTimes[group.group_number] ?? ''}
                      onChange={(v) =>
                        setLocalTeeTimes((prev) => ({
                          ...prev,
                          [group.group_number]: v,
                        }))
                      }
                    />
                    <button
                      onClick={() => saveTeeTime(group.group_number)}
                      disabled={savingTeeTime === group.group_number}
                      className="text-xs px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors disabled:opacity-50"
                    >
                      {savingTeeTime === group.group_number ? '...' : 'Save'}
                    </button>

                    <button
                      onClick={() =>
                        group.pairings.length === 0
                          ? removeEmptyGroup(group.group_number)
                          : setClearingGroup(group.group_number)
                      }
                      className="p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                      title={group.pairings.length === 0 ? 'Remove group' : 'Clear group'}
                    >
                      <TrashIcon className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {/* Players in group */}
                <div className="divide-y divide-gray-100">
                  {group.pairings.map((pairing) => {
                    const p = pairing.player;
                    if (!p) return null;
                    return (
                      <div
                        key={pairing.id}
                        className="px-4 py-2.5 flex items-center gap-3 group"
                      >
                        <div className="w-8 h-8 rounded-full bg-golf-orange/10 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-semibold text-golf-orange">
                            {p.first_name[0]}{p.last_name[0]}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-golf-gray truncate">
                            {p.first_name} {p.last_name}
                          </p>
                          <p className="text-xs text-gray-400 truncate">
                            {p.category} · HC {p.handicap} · {p.resident_club}
                          </p>
                        </div>
                        <button
                          onClick={() => setDeletingPairing(pairing)}
                          className="opacity-0 group-hover:opacity-100 p-1 rounded text-gray-300 hover:text-red-500 hover:bg-red-50 transition-all"
                        >
                          <XMarkIcon className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>

                {/* Add player to group */}
                {addingToGroup === group.group_number ? (
                  <div className="p-3 border-t border-gray-100">
                    <div className="relative mb-2">
                      <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                      <input
                        autoFocus
                        type="text"
                        value={playerSearch}
                        onChange={(e) => setPlayerSearch(e.target.value)}
                        placeholder="Search unpaired players..."
                        className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-golf-orange"
                      />
                    </div>
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {filteredSearchPlayers.length === 0 ? (
                        <p className="text-xs text-gray-400 text-center py-3">
                          {playerSearch ? 'No matches found' : 'All players are already paired'}
                        </p>
                      ) : (
                        filteredSearchPlayers.map((player) => (
                          <button
                            key={player.id}
                            onClick={() => addPlayerToGroup(group.group_number, player)}
                            className="w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-golf-orange/5 hover:text-golf-orange transition-colors flex items-center gap-2"
                          >
                            <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                              <span className="text-xs text-gray-500">
                                {player.first_name[0]}{player.last_name[0]}
                              </span>
                            </div>
                            <div className="min-w-0">
                              <span className="font-medium block truncate">
                                {player.first_name} {player.last_name}
                              </span>
                              <span className="text-xs text-gray-400 truncate block">
                                {player.category} · HC {player.handicap}
                              </span>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                    <button
                      onClick={() => {
                        setAddingToGroup(null);
                        setPlayerSearch('');
                      }}
                      className="mt-2 w-full text-xs text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div className="p-2 border-t border-gray-100">
                    <button
                      onClick={() => {
                        setAddingToGroup(group.group_number);
                        setPlayerSearch('');
                      }}
                      disabled={unpairedPlayers.length === 0}
                      className="w-full flex items-center justify-center gap-1.5 py-1.5 text-xs text-gray-400 hover:text-golf-orange hover:bg-golf-orange/5 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <PlusIcon className="w-3.5 h-3.5" />
                      Add Player
                    </button>
                  </div>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* Unpaired players summary */}
      {groups.length > 0 && unpairedPlayers.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-4"
        >
          <h4 className="text-sm font-semibold text-amber-800 mb-2">
            Unpaired Players ({unpairedPlayers.length})
          </h4>
          <div className="flex flex-wrap gap-2">
            {unpairedPlayers.map((p) => (
              <span
                key={p.id}
                className="inline-flex items-center gap-1 px-2.5 py-1 bg-white border border-amber-200 rounded-full text-xs text-amber-700"
              >
                {p.first_name} {p.last_name}
                <span className="text-amber-400">· {p.category}</span>
              </span>
            ))}
          </div>
        </motion.div>
      )}

      <DeleteConfirmationModal
        isOpen={!!deletingPairing}
        onClose={() => setDeletingPairing(null)}
        onConfirm={removePairing}
        title="Remove Player from Group"
        message={
          deletingPairing?.player
            ? `Remove ${deletingPairing.player.first_name} ${deletingPairing.player.last_name} from Group ${deletingPairing.group_number}?`
            : 'Remove this player from the group?'
        }
      />

      {showScorerAssignment && selectedRoundId && (
        <ScorerAssignmentManager
          tournamentId={tournamentId}
          roundId={selectedRoundId}
          roundName={rounds.find(r => r.id === selectedRoundId)?.name}
          onClose={() => setShowScorerAssignment(false)}
        />
      )}

      <AutoPairModal
        isOpen={showAutoPair}
        onClose={() => setShowAutoPair(false)}
        players={unpairedPlayers}
        rankedPlayers={rankedPlayers}
        onGenerate={handleAutoPair}
        generating={autoGenerating}
        hasQualifyingRound={qualifyingRoundNumber !== null}
      />

      <DeleteConfirmationModal
        isOpen={clearingGroup !== null}
        onClose={() => setClearingGroup(null)}
        onConfirm={() => clearingGroup !== null && clearGroup(clearingGroup)}
        title="Clear Group"
        message={`Remove all players from Group ${clearingGroup} and delete the group?`}
      />

      {showClearAll && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => !clearingAll && setShowClearAll(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-100 mx-auto mb-4">
              <TrashIcon className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-base font-bold text-golf-gray text-center mb-2">Clear All Pairings</h3>
            <p className="text-sm text-gray-500 text-center mb-6">
              This will remove all {groups.length} group{groups.length !== 1 ? 's' : ''} and all player assignments for the selected round. This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowClearAll(false)}
                disabled={clearingAll}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={clearAllPairings}
                disabled={clearingAll}
                className="flex-1 py-2.5 rounded-xl bg-red-500 text-white text-sm font-semibold hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {clearingAll ? (
                  <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                ) : (
                  'Clear All'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Qualifier Selection Modal */}
      {showQualifierSelection && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => !autoGenerating && setShowQualifierSelection(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div>
                <h3 className="text-lg font-bold text-golf-gray flex items-center gap-2">
                  <TrophyIcon className="w-5 h-5 text-emerald-500" />
                  Select Qualifiers for {selectedRound?.name}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Based on scores from: {qualifierSourceRounds.join(', ')} | Only players with full 18 holes in all qualifying rounds | Cuts: Men 0-18 HCP: Top 60, Men 19-28: Top 30, Ladies 0-18: Top 8, Ladies 19-36: Top 7, Veterans: Top 15
                </p>
              </div>
              <button
                onClick={() => setShowQualifierSelection(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <XMarkIcon className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            {loadingQualifiers ? (
              <div className="flex-1 flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-4 border-emerald-500 border-t-transparent" />
              </div>
            ) : qualifierPlayers.length === 0 && !manualAddSearch ? (
              <div className="flex-1 flex flex-col">
                <div className="flex items-center justify-center py-8">
                  <div className="text-center">
                    <TrophyIcon className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 font-medium">No qualifying scores available</p>
                    <p className="text-sm text-gray-400 mt-1">Use search below to manually add players to the championship round</p>
                  </div>
                </div>
                <div className="px-6 py-3 border-t border-gray-100">
                  <div className="relative">
                    <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      placeholder="Search players to add manually..."
                      value={manualAddSearch}
                      onChange={(e) => setManualAddSearch(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-amber-400"
                    />
                  </div>
                  {manualAddSearch.trim().length > 0 && (() => {
                    const pairedIds = new Set(groups.flatMap((g) => g.pairings.map((p) => p.player_id)));
                    const term = manualAddSearch.toLowerCase();
                    const matches = players.filter((p) =>
                      !pairedIds.has(p.id) &&
                      (`${p.first_name} ${p.last_name}`.toLowerCase().includes(term) ||
                        p.category.toLowerCase().includes(term))
                    ).slice(0, 8);
                    if (matches.length === 0) return (
                      <p className="text-xs text-gray-400 mt-2 px-1">No players found</p>
                    );
                    return (
                      <div className="mt-2 space-y-1 max-h-48 overflow-y-auto">
                        {matches.map((p) => (
                          <button
                            key={p.id}
                            onClick={() => {
                              const manualEntry = { ...p, total_score: 0, rounds_played: 0, rank: qualifierPlayers.length + 1 };
                              setQualifierPlayers((prev) => [...prev, manualEntry]);
                              setSelectedQualifierIds((prev) => new Set([...prev, p.id]));
                              setManualAddSearch('');
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-all hover:bg-amber-50 border border-gray-100"
                          >
                            <PlusIcon className="w-4 h-4 text-amber-500 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-700 truncate">{p.first_name} {p.last_name}</p>
                              <p className="text-[11px] text-gray-400">{p.category} &middot; HCP {p.handicap}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    );
                  })()}
                </div>
                {qualifierPlayers.length > 0 && (
                  <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between mt-auto">
                    <p className="text-sm text-gray-500">
                      <span className="font-semibold text-emerald-600">{selectedQualifierIds.size}</span> players added
                    </p>
                    <button
                      onClick={confirmQualifierSelection}
                      disabled={selectedQualifierIds.size === 0 || autoGenerating}
                      className="px-4 py-2.5 rounded-xl bg-emerald-500 text-white text-sm font-semibold hover:bg-emerald-600 transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                      {autoGenerating ? (
                        <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                      ) : (
                        <>
                          <TrophyIcon className="w-4 h-4" />
                          Pair Selected Players
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                {/* Controls */}
                <div className="px-6 py-3 border-b border-gray-100 bg-gray-50/50">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <label className="text-xs font-medium text-gray-600">Top</label>
                      <input
                        type="number"
                        min={1}
                        max={qualifierPlayers.length}
                        value={topNCutoff}
                        onChange={(e) => {
                          const n = Math.min(Math.max(1, parseInt(e.target.value) || 1), qualifierPlayers.length);
                          setTopNCutoff(n);
                          setSelectedQualifierIds(new Set(qualifierPlayers.slice(0, n).map((p) => p.id)));
                        }}
                        className="w-16 text-sm border border-gray-200 rounded-lg px-2 py-1.5 text-center focus:outline-none focus:border-emerald-400"
                      />
                      <span className="text-xs text-gray-500">of {qualifierPlayers.length} players</span>
                    </div>
                    <div className="flex gap-2 ml-auto">
                      <button
                        onClick={() => {
                          setSelectedQualifierIds(new Set(qualifierPlayers.map((p) => p.id)));
                          setTopNCutoff(qualifierPlayers.length);
                        }}
                        className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 transition-colors"
                      >
                        Select All
                      </button>
                      <button
                        onClick={() => {
                          setSelectedQualifierIds(new Set());
                          setTopNCutoff(0);
                        }}
                        className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 transition-colors"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                </div>

                {/* Manual Add Section */}
                <div className="px-6 py-3 border-b border-gray-100">
                  <div className="relative">
                    <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      placeholder="Search to manually add players..."
                      value={manualAddSearch}
                      onChange={(e) => setManualAddSearch(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-amber-400"
                    />
                  </div>
                  {manualAddSearch.trim().length > 0 && (() => {
                    const pairedIds = new Set(groups.flatMap((g) => g.pairings.map((p) => p.player_id)));
                    const qualifierIds = new Set(qualifierPlayers.map((p) => p.id));
                    const term = manualAddSearch.toLowerCase();
                    const matches = players.filter((p) =>
                      !pairedIds.has(p.id) &&
                      !qualifierIds.has(p.id) &&
                      (`${p.first_name} ${p.last_name}`.toLowerCase().includes(term) ||
                        p.category.toLowerCase().includes(term))
                    ).slice(0, 8);
                    if (matches.length === 0) return (
                      <p className="text-xs text-gray-400 mt-2 px-1">No additional players found</p>
                    );
                    return (
                      <div className="mt-2 space-y-1 max-h-36 overflow-y-auto">
                        {matches.map((p) => {
                          const alreadyAdded = selectedQualifierIds.has(p.id);
                          return (
                            <button
                              key={p.id}
                              onClick={() => {
                                if (alreadyAdded) return;
                                const manualEntry = { ...p, total_score: 0, rounds_played: 0, rank: qualifierPlayers.length + 1 };
                                setQualifierPlayers((prev) => [...prev, manualEntry]);
                                setSelectedQualifierIds((prev) => new Set([...prev, p.id]));
                                setManualAddSearch('');
                              }}
                              disabled={alreadyAdded}
                              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-all ${
                                alreadyAdded ? 'opacity-50 bg-gray-50' : 'hover:bg-amber-50 border border-gray-100'
                              }`}
                            >
                              <PlusIcon className="w-4 h-4 text-amber-500 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-700 truncate">{p.first_name} {p.last_name}</p>
                                <p className="text-[11px] text-gray-400">{p.category} &middot; HCP {p.handicap}</p>
                              </div>
                              {alreadyAdded && <span className="text-[10px] text-gray-400">Already added</span>}
                            </button>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>

                {/* Player List */}
                <div className="flex-1 overflow-y-auto px-6 py-3">
                  <div className="space-y-1">
                    {qualifierPlayers.map((player) => {
                      const isSelected = selectedQualifierIds.has(player.id);
                      const isCutLine = player.rank === topNCutoff;
                      return (
                        <div key={player.id}>
                          <button
                            onClick={() => {
                              setSelectedQualifierIds((prev) => {
                                const next = new Set(prev);
                                if (next.has(player.id)) {
                                  next.delete(player.id);
                                } else {
                                  next.add(player.id);
                                }
                                return next;
                              });
                            }}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${
                              isSelected
                                ? 'bg-emerald-50 border border-emerald-200'
                                : 'bg-white border border-gray-100 hover:border-gray-200'
                            }`}
                          >
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${
                              isSelected ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-500'
                            }`}>
                              {isSelected ? <CheckIcon className="w-3.5 h-3.5" /> : player.rank}
                            </div>
                            <div className="flex-1 text-left min-w-0">
                              <p className={`text-sm font-medium truncate ${isSelected ? 'text-emerald-800' : 'text-gray-700'}`}>
                                {player.first_name} {player.last_name}
                              </p>
                              <p className="text-[11px] text-gray-400">{player.category} &middot; HCP {player.handicap}</p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className={`text-sm font-bold ${
                                player.total_score < 0 ? 'text-green-600' : player.total_score > 0 ? 'text-red-500' : 'text-gray-700'
                              }`}>
                                {player.total_score === 0 ? 'E' : player.total_score > 0 ? `+${player.total_score}` : player.total_score}
                              </p>
                              <p className="text-[10px] text-gray-400">{player.rounds_played} rd{player.rounds_played !== 1 ? 's' : ''}</p>
                            </div>
                          </button>
                          {isCutLine && player.rank < qualifierPlayers.length && (
                            <div className="flex items-center gap-2 my-2 px-3">
                              <div className="flex-1 border-t border-dashed border-red-300" />
                              <span className="text-[10px] font-bold text-red-400 uppercase">Cut Line</span>
                              <div className="flex-1 border-t border-dashed border-red-300" />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
                  <p className="text-sm text-gray-500">
                    <span className="font-semibold text-emerald-600">{selectedQualifierIds.size}</span> players selected &middot; {Math.ceil(selectedQualifierIds.size / 4)} groups
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowQualifierSelection(false)}
                      disabled={autoGenerating}
                      className="px-4 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={confirmQualifierSelection}
                      disabled={selectedQualifierIds.size === 0 || autoGenerating}
                      className="px-4 py-2.5 rounded-xl bg-emerald-500 text-white text-sm font-semibold hover:bg-emerald-600 transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                      {autoGenerating ? (
                        <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                      ) : (
                        <>
                          <TrophyIcon className="w-4 h-4" />
                          Pair Selected Players
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
